import { startOfDay, addDays, isBefore, subDays } from 'date-fns'

import { RENTAL_FEE_PRICING_PERIOD } from 'components/pricing/RentalFees/common/settings'
import { createDateFromString, formatDateToBEFormatDateFns } from 'utils/date'

import type {
  BilledPeriodApi,
  ConfiguredRentalFeeApi,
  GracePeriodApi,
  RentalPeriodApi,
  RentalPeriodSpanApi,
} from 'types/api/rental-fee/rentalPeriod'
import type { RentalFeeDatesBody, RentalPeriodBody } from 'types/api/rental-fee/rentalPeriodRequests'
import type { IsoDateString } from 'types/entities/shared'

const { DAILY, ONCE } = RENTAL_FEE_PRICING_PERIOD

/** Any dated span the calendar reasons over — API periods and locally generated ones alike. */
type DateSpan = RentalPeriodSpanApi

/** A period the user can select: API rows always carry an id, payload rows not until saved. */
type IdentifiedPeriod = RentalPeriodBody

type DateRange = { earliestStartDate: IsoDateString | null; latestEndDate: IsoDateString | null }

type BillingStatus = 'UNBILLED' | 'GRACE_PERIOD' | 'BILLED'

type BillingStatusByDate = Record<string, BillingStatus>

type BillingData = {
  unbilledPeriods: DateSpan[]
  gracePeriods: DateSpan[]
}

type FullBillingData = BillingData & { billedPeriods: BilledPeriodApi[] }

type PendingPeriodEdit = {
  periodId: string
  startDateBE: IsoDateString | null
  endDateBE: IsoDateString | null
}

const forEachDateInRange = (
  startDateStr: IsoDateString | null,
  endDateStr: IsoDateString | null,
  callback: (dateStr: IsoDateString) => void
): void => {
  const start = createDateFromString(startDateStr)
  const end = createDateFromString(endDateStr)
  if (!start || !end) return

  let current = new Date(start)
  while (current <= end) {
    callback(formatDateToBEFormatDateFns(current))
    current = addDays(current, 1)
  }
}

export const getRentalPeriodDateRange = (rentalPeriods: DateSpan[] = []): DateRange => {
  if (!rentalPeriods.length) {
    return { earliestStartDate: null, latestEndDate: null }
  }

  return rentalPeriods.reduce<DateRange>(
    (acc, period) => {
      if (period.startDate && (!acc.earliestStartDate || period.startDate < acc.earliestStartDate)) {
        acc.earliestStartDate = period.startDate
      }
      if (period.endDate && (!acc.latestEndDate || period.endDate > acc.latestEndDate)) {
        acc.latestEndDate = period.endDate
      }
      return acc
    },
    { earliestStartDate: null, latestEndDate: null }
  )
}

export const getLatestRentalPeriod = (rentalPeriods: RentalPeriodApi[] = []): IdentifiedPeriod => {
  if (!rentalPeriods.length) {
    return { id: null, startDate: null, endDate: null }
  }

  return rentalPeriods.reduce<IdentifiedPeriod>(
    (latest, period) => {
      if (!latest.startDate || (period.startDate && period.startDate > latest.startDate)) {
        return { id: period.id, startDate: period.startDate, endDate: period.endDate }
      }
      return latest
    },
    { id: null, startDate: null, endDate: null }
  )
}

export const getEarliestFuturePeriodStartDate = (rentalPeriods: DateSpan[], afterDate: Date | null): Date | null => {
  if (!rentalPeriods.length || !afterDate) return null

  const afterDateStr = formatDateToBEFormatDateFns(afterDate)

  const futureStartDates = rentalPeriods
    .map(period => period.startDate)
    .filter((startDate): startDate is IsoDateString => Boolean(startDate) && (startDate as IsoDateString) > afterDateStr)

  if (!futureStartDates.length) return null

  const earliest = futureStartDates.reduce((min, startDate) => (startDate < min ? startDate : min))

  return createDateFromString(earliest)
}

export const isClosedPeriod = (period: DateSpan | null | undefined): boolean => Boolean(period?.startDate && period?.endDate)

export const isOngoingPeriod = (period: DateSpan | null | undefined): boolean => Boolean(period?.startDate && !period?.endDate)

export const isValidRentalPeriod = (period: DateSpan | null | undefined): boolean => {
  if (!period) return false
  if (!period.endDate) return true
  if (!period.startDate) return true
  return period.startDate <= period.endDate
}

export const filterValidRentalPeriods = <T extends DateSpan>(periods: T[] = []): T[] => periods.filter(isValidRentalPeriod)

export const findPeriodForDate = <T extends DateSpan>(dateStr: IsoDateString | null, rentalPeriods: T[] = []): T | null => {
  if (!dateStr || !rentalPeriods.length) return null

  return (
    rentalPeriods.find(({ startDate, endDate }) => {
      if (!startDate) return false
      const isAfterStart = dateStr >= startDate
      const isBeforeOrAtEnd = !endDate || dateStr <= endDate
      return isAfterStart && isBeforeOrAtEnd
    }) || null
  )
}

/** Boundary day: prefer the period that starts here so Edit opens that one. */
export const findPeriodForDatePreferStart = <T extends DateSpan>(dateStr: IsoDateString | null, rentalPeriods: T[] = []): T | null => {
  if (!dateStr || !rentalPeriods.length) return null

  const periodWhereDateIsStart = rentalPeriods.find(p => p.startDate === dateStr)
  if (periodWhereDateIsStart) return periodWhereDateIsStart

  return findPeriodForDate(dateStr, rentalPeriods)
}

export const canDateBeNewPeriodStart = (dateStr: IsoDateString | null, rentalPeriods: DateSpan[] = []): boolean => {
  if (!dateStr) return false
  return !findPeriodForDate(dateStr, rentalPeriods)
}

const ONGOING_PERIOD_END_SENTINEL = '9999-12-31'

/** An open period covers every day after its start, so its span has no end to compare against. */
export const getPeriodSpanEnd = (period: DateSpan): IsoDateString => period.endDate || ONGOING_PERIOD_END_SENTINEL

/** Touching is not overlap. A missing end is open, via a far-future sentinel. */
export const doRentalPeriodRangesOverlap = (
  startA: IsoDateString | null,
  endA: IsoDateString | null,
  startB: IsoDateString | null,
  endB: IsoDateString | null
): boolean => {
  if (!startA || !startB) return false
  const effectiveEndA = endA || ONGOING_PERIOD_END_SENTINEL
  const effectiveEndB = endB || ONGOING_PERIOD_END_SENTINEL
  return startA < effectiveEndB && startB < effectiveEndA
}

export const wouldPeriodOverlap = (
  newStartBE: IsoDateString | null,
  newEndBE: IsoDateString | null,
  periods: IdentifiedPeriod[] = [],
  excludeId: string | null = null
): boolean => {
  if (!newStartBE || !periods.length) return false
  return periods.some(period => {
    if (period.id === excludeId || !period.startDate) return false
    return doRentalPeriodRangesOverlap(newStartBE, newEndBE, period.startDate, period.endDate)
  })
}

export const hasOverlappingPeriods = (periods: DateSpan[] = []): boolean => {
  if (!periods.length) return false
  const withStart = periods.filter((p): p is DateSpan & { startDate: IsoDateString } => Boolean(p.startDate))
  if (withStart.length < 2) return false

  const sorted = withStart.slice().sort((a, b) => {
    if (a.startDate === b.startDate) return 0
    return a.startDate < b.startDate ? -1 : 1
  })

  for (let i = 1; i < sorted.length; i++) {
    const prev = sorted[i - 1]
    const curr = sorted[i]
    if (doRentalPeriodRangesOverlap(prev.startDate, prev.endDate, curr.startDate, curr.endDate)) return true
  }
  return false
}

export const getNewPeriodFillEndDate = (newStartDate: Date, rentalPeriods: DateSpan[] = []): Date => {
  const today = startOfDay(new Date())
  const nextPeriodStart = getEarliestFuturePeriodStartDate(rentalPeriods, newStartDate)

  if (nextPeriodStart && isBefore(nextPeriodStart, today)) {
    return subDays(nextPeriodStart, 1)
  }

  return today
}

export const generateBillingData = (startDate: Date | null, gracePeriodDays = 0, endDate: Date | null = null): BillingData => {
  const emptyResult: BillingData = { unbilledPeriods: [], gracePeriods: [] }

  if (!startDate) return emptyResult

  const normalizedStartDate = startOfDay(startDate)
  const today = startOfDay(new Date())
  const normalizedEndDate = endDate ? startOfDay(endDate) : today
  const fillUntilDate = normalizedEndDate

  if (isBefore(fillUntilDate, normalizedStartDate)) {
    return emptyResult
  }

  const gracePeriods: DateSpan[] = []
  const unbilledPeriods: DateSpan[] = []

  if (gracePeriodDays > 0) {
    const graceEndDateRaw = subDays(addDays(normalizedStartDate, gracePeriodDays), 1)
    const graceEndDate = isBefore(graceEndDateRaw, fillUntilDate) ? graceEndDateRaw : fillUntilDate

    gracePeriods.push({
      startDate: formatDateToBEFormatDateFns(normalizedStartDate),
      endDate: formatDateToBEFormatDateFns(graceEndDate),
    })

    const unbilledStartDate = addDays(graceEndDate, 1)

    if (!isBefore(fillUntilDate, unbilledStartDate)) {
      unbilledPeriods.push({
        startDate: formatDateToBEFormatDateFns(unbilledStartDate),
        endDate: formatDateToBEFormatDateFns(fillUntilDate),
      })
    }
  } else {
    unbilledPeriods.push({
      startDate: formatDateToBEFormatDateFns(normalizedStartDate),
      endDate: formatDateToBEFormatDateFns(fillUntilDate),
    })
  }

  return { unbilledPeriods, gracePeriods }
}

export const regenerateBillingDataForEffectivePeriods = (
  rentalPeriods: RentalPeriodApi[],
  activePeriodId: string | null,
  formStartBE: IsoDateString | null,
  formEndBE: IsoDateString | null | undefined,
  gracePeriodDays = 0,
  existingBilledPeriods: BilledPeriodApi[] = []
): FullBillingData => {
  const today = startOfDay(new Date())
  const unbilledPeriods: DateSpan[] = []
  const gracePeriods: DateSpan[] = []

  rentalPeriods.forEach(period => {
    const isEdited = period.id === activePeriodId
    let startDate: Date | null = null
    if (isEdited && formStartBE) {
      startDate = createDateFromString(formStartBE)
    } else if (period.startDate) {
      startDate = createDateFromString(period.startDate)
    }
    if (!startDate) return

    let endDate: Date | null = null
    if (isEdited && formEndBE !== undefined) {
      endDate = formEndBE ? createDateFromString(formEndBE) : null
    } else if (period.endDate) {
      endDate = createDateFromString(period.endDate)
    }
    const fillUntilDate = endDate || today

    const data = generateBillingData(startDate, gracePeriodDays, fillUntilDate)
    unbilledPeriods.push(...data.unbilledPeriods)
    gracePeriods.push(...data.gracePeriods)
  })

  return {
    unbilledPeriods,
    gracePeriods,
    billedPeriods: existingBilledPeriods,
  }
}

export const getBaseBillingData = (
  pendingPeriodEdit: PendingPeriodEdit | null,
  existingRentalFee: ConfiguredRentalFeeApi | null,
  gracePeriodDays: number
): FullBillingData => {
  if (pendingPeriodEdit) {
    return regenerateBillingDataForEffectivePeriods(
      existingRentalFee?.rentalPeriods ?? [],
      pendingPeriodEdit.periodId,
      pendingPeriodEdit.startDateBE,
      pendingPeriodEdit.endDateBE,
      gracePeriodDays,
      existingRentalFee?.billedPeriods ?? []
    )
  }
  return {
    unbilledPeriods: existingRentalFee?.unbilledPeriods ?? [],
    billedPeriods: existingRentalFee?.billedPeriods ?? [],
    gracePeriods: existingRentalFee?.gracePeriods ?? [],
  }
}

export const buildBillingStatusByDate = (
  unbilledPeriods: DateSpan[] = [],
  billedPeriods: DateSpan[] = [],
  gracePeriods: DateSpan[] = []
): BillingStatusByDate => {
  const statusMap: BillingStatusByDate = {}

  const addPeriodToMap = (periods: DateSpan[], status: BillingStatus) => {
    periods.forEach(({ startDate, endDate }) => {
      forEachDateInRange(startDate, endDate, dateStr => {
        statusMap[dateStr] = status
      })
    })
  }

  addPeriodToMap(unbilledPeriods, 'UNBILLED')
  addPeriodToMap(gracePeriods, 'GRACE_PERIOD')
  addPeriodToMap(billedPeriods, 'BILLED')

  return statusMap
}

/** An ongoing period only counts through today here, not the far-future sentinel. */
export const isDateInAnyRentalPeriod = (
  dateStr: IsoDateString | null,
  rentalPeriods: DateSpan[] = []
): { found: boolean; startDate: IsoDateString | null; endDate: IsoDateString | null } => {
  if (!dateStr || !rentalPeriods.length) return { found: false, startDate: null, endDate: null }
  const today = startOfDay(new Date())

  const matchingPeriod = rentalPeriods.find(({ startDate, endDate }) => {
    if (!startDate) return false
    const isAfterStart = dateStr >= startDate
    const isBeforeOrAtEnd = endDate ? dateStr <= endDate : dateStr <= formatDateToBEFormatDateFns(today)
    return isAfterStart && isBeforeOrAtEnd
  })
  if (matchingPeriod) {
    return { found: true, startDate: matchingPeriod.startDate, endDate: matchingPeriod.endDate || formatDateToBEFormatDateFns(today) }
  }

  return { found: false, startDate: null, endDate: null }
}

/** Start/end days are always in the set; interior days only if they have billing status. */
export const buildRentalPeriodData = (
  rentalPeriods: DateSpan[] = [],
  billingStatusByDate: BillingStatusByDate = {}
): { rentalPeriodDates: Set<string>; rentalPeriodStartDates: Set<string>; rentalPeriodEndDates: Set<string> } => {
  const rentalPeriodDates = new Set<string>()
  const rentalPeriodStartDates = new Set<string>()
  const rentalPeriodEndDates = new Set<string>()

  rentalPeriods.forEach(({ startDate, endDate }) => {
    const start = createDateFromString(startDate)
    const end = createDateFromString(endDate)
    if (!start) return

    const startDateStr = formatDateToBEFormatDateFns(start)
    const endDateStr = end ? formatDateToBEFormatDateFns(end) : null

    rentalPeriodStartDates.add(startDateStr)
    rentalPeriodDates.add(startDateStr)

    if (endDateStr) {
      rentalPeriodEndDates.add(endDateStr)
      rentalPeriodDates.add(endDateStr)
    }

    const effectiveEnd = end || start
    let current = addDays(new Date(start), 1)

    while (current < effectiveEnd) {
      const dateStr = formatDateToBEFormatDateFns(current)
      if (billingStatusByDate[dateStr]) {
        rentalPeriodDates.add(dateStr)
      }
      current = addDays(current, 1)
    }
  })

  return { rentalPeriodDates, rentalPeriodStartDates, rentalPeriodEndDates }
}

export const getPeriodKey = (date: Date, periodType: string | undefined): string => {
  const year = date.getFullYear()
  const month = date.getMonth()

  switch (periodType) {
    case 'WEEKLY': {
      const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()))
      const dayNum = d.getUTCDay() || 7
      d.setUTCDate(d.getUTCDate() + 4 - dayNum)
      const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1))
      const weekNo = Math.ceil(((d.getTime() - yearStart.getTime()) / 86400000 + 1) / 7)
      return `${d.getUTCFullYear()}-W${weekNo}`
    }
    case 'MONTHLY':
      return `${year}-${month}`
    case 'QUARTERLY':
      return `${year}-Q${Math.floor(month / 3)}`
    case 'ANNUALLY':
      return `${year}`
    default:
      return formatDateToBEFormatDateFns(date)
  }
}

const buildGracePeriodDatesSet = (gracePeriods: DateSpan[] = []): Set<string> => {
  const graceSet = new Set<string>()
  gracePeriods.forEach(({ startDate, endDate }) => {
    forEachDateInRange(startDate, endDate, dateStr => graceSet.add(dateStr))
  })
  return graceSet
}

/** Full price once per pricing period, on its last billable day. ONCE is empty — that badge lives on the fee end date. */
export const buildDayPriceByDate = (
  billedPeriods: DateSpan[],
  unbilledPeriods: DateSpan[],
  totalPrice: number,
  pricingPeriod: string | undefined,
  rentalPeriods: DateSpan[] = [],
  gracePeriods: DateSpan[] = []
): Record<string, number> => {
  const priceMap: Record<string, number> = {}
  const periodType = pricingPeriod?.toUpperCase()

  if (periodType === ONCE) return priceMap

  const gracePeriodDatesSet = buildGracePeriodDatesSet(gracePeriods)

  const billableDatesFromApi = new Set<string>()
  const allBillingPeriods = [...billedPeriods, ...unbilledPeriods]
  allBillingPeriods.forEach(({ startDate, endDate }) => {
    forEachDateInRange(startDate, endDate, dateStr => billableDatesFromApi.add(dateStr))
  })

  const futureBillableDates = new Set<string>()
  rentalPeriods.forEach(({ startDate, endDate }) => {
    if (!endDate) return
    forEachDateInRange(startDate, endDate, dateStr => {
      if (!billableDatesFromApi.has(dateStr) && !gracePeriodDatesSet.has(dateStr)) {
        futureBillableDates.add(dateStr)
      }
    })
  })

  const allBillableDates = new Set([...Array.from(billableDatesFromApi), ...Array.from(futureBillableDates)])

  if (periodType === DAILY) {
    allBillableDates.forEach(dateStr => {
      priceMap[dateStr] = totalPrice
    })
    return priceMap
  }

  const lastDatePerPeriod: Record<string, string> = {}

  allBillableDates.forEach(dateStr => {
    const date = createDateFromString(dateStr)
    if (!date) return

    const periodKey = getPeriodKey(date, periodType)

    if (!lastDatePerPeriod[periodKey] || dateStr > lastDatePerPeriod[periodKey]) {
      lastDatePerPeriod[periodKey] = dateStr
    }
  })

  Object.values(lastDatePerPeriod).forEach(dateStr => {
    priceMap[dateStr] = totalPrice
  })

  return priceMap
}

export const buildRentalPeriodsPayload = (
  existingPeriods: IdentifiedPeriod[],
  activePeriodId: string | null,
  newStartDate: IsoDateString | null,
  newEndDate: IsoDateString | null
): RentalPeriodBody[] => {
  if (activePeriodId) {
    return existingPeriods.map(period => {
      if (period.id === activePeriodId) {
        return {
          id: period.id,
          startDate: newStartDate,
          endDate: newEndDate,
        }
      }
      return {
        id: period.id,
        startDate: period.startDate,
        endDate: period.endDate,
      }
    })
  }

  const existingPeriodsPayload: RentalPeriodBody[] = existingPeriods.map(period => ({
    id: period.id,
    startDate: period.startDate,
    endDate: period.endDate,
  }))

  if (newStartDate) {
    existingPeriodsPayload.push({
      startDate: newStartDate,
      endDate: newEndDate,
    })
  }

  return existingPeriodsPayload
}

export const getExistingFeeDatesBE = (
  existingRentalFee: ConfiguredRentalFeeApi | null
): { existingFeeStartBE: IsoDateString | null; existingFeeEndBE: IsoDateString | null } => {
  if (!existingRentalFee) {
    return { existingFeeStartBE: null, existingFeeEndBE: null }
  }
  const existingFeeStartBE = existingRentalFee.startDate
    ? formatDateToBEFormatDateFns(createDateFromString(existingRentalFee.startDate))
    : null
  const existingFeeEndBE = existingRentalFee.endDate ? formatDateToBEFormatDateFns(createDateFromString(existingRentalFee.endDate)) : null
  return { existingFeeStartBE, existingFeeEndBE }
}

export const getNewPeriodDateRange = ({
  currentFee,
  validPeriods,
  formattedStart,
  formattedEnd,
  existingFeeStartBE,
  existingFeeEndBE,
  existingRentalFee,
  isCreatingNewPeriod,
}: {
  currentFee: { activePeriodId: string | null }
  validPeriods: IdentifiedPeriod[]
  formattedStart: IsoDateString | null
  formattedEnd: IsoDateString | null
  existingFeeStartBE: IsoDateString | null
  existingFeeEndBE: IsoDateString | null
  existingRentalFee: ConfiguredRentalFeeApi | null
  isCreatingNewPeriod: boolean
}): { newPeriodStart: IsoDateString | null; newPeriodEnd: IsoDateString | null } => {
  const activePeriod = currentFee.activePeriodId ? validPeriods.find(p => p.id === currentFee.activePeriodId) : null
  if (currentFee.activePeriodId && activePeriod) {
    return { newPeriodStart: formattedStart, newPeriodEnd: formattedEnd }
  }
  const formDatesMatchFeeDates = existingRentalFee && formattedStart === existingFeeStartBE && formattedEnd === existingFeeEndBE
  const includeNewPeriodDates = Boolean(currentFee.activePeriodId) || (isCreatingNewPeriod && !formDatesMatchFeeDates)
  if (!includeNewPeriodDates) {
    return { newPeriodStart: null, newPeriodEnd: null }
  }
  return { newPeriodStart: formattedStart, newPeriodEnd: formattedEnd }
}

export const buildRentalFeeSaveBody = (
  validPeriods: IdentifiedPeriod[],
  activePeriodId: string | null,
  newPeriodStart: IsoDateString | null,
  newPeriodEnd: IsoDateString | null
): RentalFeeDatesBody => {
  const rentalPeriods = buildRentalPeriodsPayload(validPeriods, activePeriodId, newPeriodStart, newPeriodEnd)
  const { earliestStartDate, latestEndDate } = getRentalPeriodDateRange(rentalPeriods)
  return {
    startDate: earliestStartDate,
    endDate: latestEndDate,
    rentalPeriods,
  }
}

export const buildDeletePeriodPayload = (existingPeriods: IdentifiedPeriod[], periodIdToDelete: string): RentalFeeDatesBody => {
  const remainingPeriods = existingPeriods
    .filter(p => p.id !== periodIdToDelete)
    .map(({ id, startDate, endDate }) => ({ id, startDate, endDate }))

  const { earliestStartDate, latestEndDate } = getRentalPeriodDateRange(remainingPeriods)
  return {
    startDate: earliestStartDate,
    endDate: latestEndDate,
    rentalPeriods: remainingPeriods,
  }
}

const findAdjacentPeriodsAtDate = (
  dateStr: IsoDateString | null,
  rentalPeriods: RentalPeriodApi[] = []
): { periodBefore: RentalPeriodApi | null; periodAfter: RentalPeriodApi | null } => {
  if (!dateStr || !rentalPeriods.length) {
    return { periodBefore: null, periodAfter: null }
  }

  const periodBefore = rentalPeriods.find(period => period.endDate === dateStr) ?? null
  const periodAfter = rentalPeriods.find(period => period.startDate === dateStr) ?? null

  return { periodBefore, periodAfter }
}

/** Undo-reset takes the history ids of grace periods that contain this date. */
export const getRentalFeeResetHistoryIdsForBoundaryDate = (
  dateStr: IsoDateString | null,
  gracePeriods: GracePeriodApi[] = []
): string[] => {
  if (!dateStr || !Array.isArray(gracePeriods)) return []

  const ids: string[] = []
  gracePeriods.forEach(gracePeriod => {
    const start = gracePeriod.startDate
    const end = gracePeriod.endDate
    if (!start || !end) return
    if (dateStr < start || dateStr > end) return
    if (gracePeriod.rentalFeeResetHistoryId) {
      ids.push(gracePeriod.rentalFeeResetHistoryId)
    }
  })
  return ids
}

/** Undo is only available at a closed/open period join that still carries a reset-history id. */
export const canRevertGracePeriodReset = (
  dateStr: IsoDateString | null,
  rentalPeriods: RentalPeriodApi[] = [],
  gracePeriods: GracePeriodApi[] = []
): boolean => {
  const { periodBefore, periodAfter } = findAdjacentPeriodsAtDate(dateStr, rentalPeriods)

  if (!periodBefore || !periodAfter) return false
  if (!isClosedPeriod(periodBefore)) return false

  const resetHistoryIds = getRentalFeeResetHistoryIdsForBoundaryDate(dateStr, gracePeriods)
  return resetHistoryIds.length > 0
}

/** YYYY-MM-DD compares lexicographically as chronology. */
export const hasBilledDaysInRange = (
  rangeStart: IsoDateString | null,
  rangeEnd: IsoDateString | null,
  billedPeriods: DateSpan[] = []
): boolean => {
  if (!rangeStart || !rangeEnd || !billedPeriods.length) return false
  return billedPeriods.some(({ startDate, endDate }) => {
    if (!startDate || !endDate) return false
    return rangeStart <= endDate && startDate <= rangeEnd
  })
}

type InvoiceEntry = { invoiceNumber: number; invoiceStatus: string | null }

type InvoiceInfo = { invoiceIds: string[]; invoiceEntries: InvoiceEntry[] }

/** Only the field the calendar groups work orders by. */
type WorkOrderServiceDate = { serviceDate: IsoDateString | null }

const DRAFT_INVOICE_STATUS = 'Draft'
const FINALIZED_INVOICE_STATUSES = new Set(['Posted', 'Sent', 'Unpaid', 'Paid'])

export const getFinalizedBilledPeriods = (billedPeriods: BilledPeriodApi[] = []): BilledPeriodApi[] =>
  billedPeriods.filter(bp => bp.invoiceStatus && FINALIZED_INVOICE_STATUSES.has(bp.invoiceStatus))

export const getDraftBilledPeriods = (billedPeriods: BilledPeriodApi[] = []): BilledPeriodApi[] =>
  billedPeriods.filter(bp => bp.invoiceStatus === DRAFT_INVOICE_STATUS)

export const hasFinalizedBilledDaysInRange = (
  rangeStart: IsoDateString | null,
  rangeEnd: IsoDateString | null,
  billedPeriods: BilledPeriodApi[] = []
): boolean => hasBilledDaysInRange(rangeStart, rangeEnd, getFinalizedBilledPeriods(billedPeriods))

/**
 * invoice_number is NOT NULL on hh_invoice, so an invoiceId with no number means the lookup
 * found nothing — charges stayed billed after the invoice was deleted.
 */
export const getOrphanedBilledPeriods = (billedPeriods: BilledPeriodApi[] = []): BilledPeriodApi[] =>
  billedPeriods.filter(billedPeriod => billedPeriod.invoiceId && billedPeriod.invoiceNumber == null)

export const hasOrphanedBilledDaysInRange = (
  rangeStart: IsoDateString | null,
  rangeEnd: IsoDateString | null,
  billedPeriods: BilledPeriodApi[] = []
): boolean => hasBilledDaysInRange(rangeStart, rangeEnd, getOrphanedBilledPeriods(billedPeriods))

const getInvoiceInfoForRange = (rangeStart: IsoDateString, rangeEnd: IsoDateString, filteredPeriods: BilledPeriodApi[]): InvoiceInfo => {
  const idSet = new Set<string>()
  const seen = new Set<number>()
  const entries: InvoiceEntry[] = []
  filteredPeriods.forEach(({ startDate, endDate, invoiceId, invoiceNumber, invoiceStatus }) => {
    if (!startDate || !endDate) return
    if (rangeStart <= endDate && startDate <= rangeEnd) {
      if (invoiceId) idSet.add(invoiceId)
      if (invoiceNumber != null && !seen.has(invoiceNumber)) {
        seen.add(invoiceNumber)
        entries.push({ invoiceNumber, invoiceStatus })
      }
    }
  })
  return { invoiceIds: Array.from(idSet), invoiceEntries: entries }
}

export const getDraftInvoiceInfoForRange = (
  rangeStart: IsoDateString | null,
  rangeEnd: IsoDateString | null,
  billedPeriods: BilledPeriodApi[] = []
): { invoiceIds: string[]; invoiceNumbers: number[] } => {
  if (!rangeStart || !rangeEnd || !billedPeriods.length) return { invoiceIds: [], invoiceNumbers: [] }
  const { invoiceIds, invoiceEntries } = getInvoiceInfoForRange(rangeStart, rangeEnd, getDraftBilledPeriods(billedPeriods))
  return { invoiceIds, invoiceNumbers: invoiceEntries.map(e => e.invoiceNumber) }
}

export const getFinalizedInvoiceInfoForRange = (
  rangeStart: IsoDateString | null,
  rangeEnd: IsoDateString | null,
  billedPeriods: BilledPeriodApi[] = []
): InvoiceInfo => {
  if (!rangeStart || !rangeEnd || !billedPeriods.length) return { invoiceIds: [], invoiceEntries: [] }
  return getInvoiceInfoForRange(rangeStart, rangeEnd, getFinalizedBilledPeriods(billedPeriods))
}

/**
 * Mirrors BILLED_DAYS_AFFECTED in crm-core RentalFeeServiceImpl. An open period covers every day
 * after its start, so ending one mid-way is checked against all of it — capping at today let a
 * later billed month through. Draft-invoiced days may shrink; generation rewrites them.
 */
export const wouldChangesAffectBilledDays = (
  originalPeriods: IdentifiedPeriod[],
  proposedPeriods: IdentifiedPeriod[],
  billedPeriods: BilledPeriodApi[]
): boolean =>
  originalPeriods.some(original => {
    if (!original.startDate) return false
    const originalEnd = getPeriodSpanEnd(original)
    const proposed = proposedPeriods.find(p => p.id === original.id)
    if (!proposed) {
      return hasFinalizedBilledDaysInRange(original.startDate, originalEnd, billedPeriods)
    }
    const { startDate: proposedStartBE, endDate: proposedEndBE } = proposed
    const proposedStart = proposedStartBE ? createDateFromString(proposedStartBE) : null
    if (proposedStartBE && proposedStart && proposedStartBE > original.startDate) {
      const dayBefore = formatDateToBEFormatDateFns(subDays(proposedStart, 1))
      if (hasFinalizedBilledDaysInRange(original.startDate, dayBefore, billedPeriods)) return true
    }
    const proposedEnd = proposedEndBE ? createDateFromString(proposedEndBE) : null
    if (proposedEndBE && proposedEnd && proposedEndBE < originalEnd) {
      const dayAfter = formatDateToBEFormatDateFns(addDays(proposedEnd, 1))
      if (hasFinalizedBilledDaysInRange(dayAfter, originalEnd, billedPeriods)) return true
    }
    return false
  })

export const DEFAULT_RENTAL_FEE = {
  rentalFeeToConfiguredServiceId: null,
  rentalFeeId: '',
  feeName: '',
  pricingPeriod: '',
  gracePeriodDays: 0,
  startDate: null,
  endDate: null,
  amount: 0,
  billingData: {
    unbilledPeriods: [],
    billedPeriods: [],
    gracePeriods: [],
  },
  rentalPeriods: [],
  activePeriodId: null,
}

export const buildEventsByDate = (workOrders: WorkOrderServiceDate[] = []): Record<string, number> => {
  if (!workOrders.length) return {}

  return workOrders.reduce<Record<string, number>>((acc, workOrder) => {
    const { serviceDate } = workOrder
    if (serviceDate) {
      acc[serviceDate] = (acc[serviceDate] || 0) + 1
    }
    return acc
  }, {})
}

export const filterWorkOrdersByDate = <T extends WorkOrderServiceDate>(selectedDay: Date | null, workOrders: T[] = []): T[] => {
  if (!selectedDay) return []
  const selectedDateStr = formatDateToBEFormatDateFns(selectedDay)
  return workOrders.filter(wo => wo.serviceDate === selectedDateStr)
}

export const getRentalFeeResetCount = (resetHistoryData: { resets?: unknown[] }[] = []): number => {
  if (!Array.isArray(resetHistoryData)) return 0
  return resetHistoryData.reduce((count, item) => count + (item.resets?.length || 0), 0)
}
